import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query';
import { useState, useEffect } from 'react';
import {
  GroupMemberRoleEnum,
  GroupDto,
  GroupMemberRole,
  GroupsDto,
  CreateOrganizationMutationData,
  UpdateOrganizationMutationData,
  UploadExternalContactsMutationData,
  GetExternalContactsCountDto,
  GroupWithMetadata,
  MessageExternalContactsMutationData,
  GroupMembersDto,
  AssociationDto,
  AssociationDtoWithMetadata,
  AssociationMembersDto,
  UpdateAssociationMutationData,
  AssociationRequestsToPublishDto,
  AlbumDto,
  AlbumsDto,
  CreateAlbumDto,
  GroupMemberDto,
  AssociationEventLogDto,
  AssociationEventLogSortBy,
  AssociationPublishRequestSortBy,
  AssociationsDto
} from 'legacy/groups/types';

import {
  createGroup,
  getAssociation,
  getExternalContactsCount,
  getGroup,
  getGroups,
  getGroupMembers,
  getPendingGroupMembers,
  getGroupEvents,
  messageExternalContacts,
  updateGroup,
  uploadExternalContacts,
  removeMember,
  changeMemberRole,
  getAssociationMembers,
  getAssociationMemberRequests,
  getAssociationAdmins,
  getAssociationAffiliatedMembers,
  updateAssociationMemberStatus,
  updateAssociation,
  removeAssociationMember,
  confirmStudentEmail,
  confirmEmailCode,
  getAssociationRequestsToPublish,
  listGroups,
  listAssociations,
  createGroupAlbum,
  getGroupAlbums,
  approveMemberRequest,
  rejectMemberRequest,
  getGroupAlbumMoments,
  getGroupAlbum,
  updateGroupAlbum,
  deleteGroupAlbum,
  getAssociationEventsLog,
  listOwnAssociations,
  getUnauthorizedGroup,
  getUnauthorizedAssociation,
  getAssociationVerificationStatus,
  generateAssociationRequirementsLink
} from './service';
import { ErrorResponse } from 'legacy/common/utils/error';
import { GenericSuccessResponse } from 'legacy/common/types/response';
import { queryClient } from 'legacy/common/utils/react-query';
import { usePagination } from 'hooks/usePagination';
import { EventData } from 'components/EventsCalendar/types';
import { useCursorQuery } from 'helpers/queries/useCursorQuery';
import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';

export function useGetGroups({ role }: { role?: GroupMemberRole[] }) {
  return useQuery<GroupsDto, ErrorResponse>(['groups', { role }], () =>
    getGroups({ role })
  );
}

export function useGetGroupAlbums({ groupId }: { groupId: string }) {
  const queryClient = useQueryClient();
  const limit = 20;
  const [totalPages, setTotalPages] = useState(1);

  const {
    currentPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage
  } = usePagination(totalPages);

  const { data, ...rest } = useQuery<AlbumsDto, ErrorResponse>(
    ['groupAlbums', { groupId, currentPage, limit }],
    () => getGroupAlbums({ groupId, pageNumber: currentPage, limit }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        const calculatedTotalPages = data.totalPages;
        setTotalPages(calculatedTotalPages);
      }
    }
  );

  const refreshAlbums = () => {
    queryClient.invalidateQueries(['groupAlbums', { groupId }]);
  };

  return {
    ...rest,
    data: data?.items || [],
    hasNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    fetchNextPage,
    refreshAlbums
  };
}

export function useGetGroupAlbumMoments({
  groupId,
  albumId
}: {
  groupId: string;
  albumId: string;
}) {
  const limit = 20;
  const [totalPages, setTotalPages] = useState(1);

  const {
    currentPage,
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage
  } = usePagination(totalPages);

  const { data, ...rest } = useQuery<AlbumsDto, ErrorResponse>(
    ['groupAlbumMoments', { groupId, albumId, currentPage, limit }],
    () =>
      getGroupAlbumMoments({
        groupId,
        albumId,
        pageNumber: currentPage,
        limit
      }),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        const calculatedTotalPages = data.totalPages;
        setTotalPages(calculatedTotalPages);
      }
    }
  );

  return {
    ...rest,
    data: data?.items || [],
    hasNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    fetchNextPage
  };
}

export function useGetGroupAlbum({
  groupId,
  albumId
}: {
  groupId: string;
  albumId: string;
}) {
  const { data, ...rest } = useQuery<AlbumDto, ErrorResponse>(
    ['groupAlbum', { groupId, albumId }],
    () =>
      getGroupAlbum({
        groupId,
        albumId
      })
  );

  const refreshAlbum = () => {
    queryClient.invalidateQueries(['groupAlbum', { groupId, albumId }]);
  };

  return {
    ...rest,
    data,
    refreshAlbum
  };
}

export function useListAssociations(search?: string) {
  const listAssociationsData = useInfiniteQuery<AssociationsDto, ErrorResponse>(
    ['list/associations', { search }],
    ({ pageParam }) => listAssociations({ search, pageNumber: pageParam }),
    {
      getNextPageParam: (data) => {
        if (data.currentPage + 1 < data.totalPages) {
          return data.currentPage + 1;
        }
        return undefined;
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Can't figure this one out. I properly typed on the return of the hook.
      select: (data) => data?.pages?.map((page) => page.items).flat() || []
    }
  );

  return {
    ...listAssociationsData,
    data: (listAssociationsData.data || []) as AssociationDto[]
  };
}

export function useListOwnAssociations() {
  const listAssociationsData = useInfiniteQuery<
    AssociationDto[],
    ErrorResponse
  >(['list/associations/me'], () => listOwnAssociations(), {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore - Can't figure this one out. I properly typed on the return of the hook.
    select: (data) => data?.pages?.map((page) => page.items).flat() || []
  });

  return {
    ...listAssociationsData,
    data: (listAssociationsData.data || []) as AssociationDto[]
  };
}

export function useListGroups({ search }: { search?: string }) {
  const listGroupsData = useInfiniteQuery<GroupsDto, ErrorResponse>(
    ['list/groups', { search }],
    ({ pageParam }) => listGroups({ search, pageNumber: pageParam }),
    {
      getNextPageParam: (data) => {
        if (data.currentPage + 1 < data.totalPages) {
          return data.currentPage + 1;
        }
        return undefined;
      },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - Can't figure this one out. I properly typed on the return of the hook.
      select: (data) => data?.pages?.map((page) => page.items).flat() || []
    }
  );

  return {
    ...listGroupsData,
    data: (listGroupsData.data || []) as GroupDto[]
  };
}

export function useGetGroup({ groupId }: { groupId: string }) {
  const { isAuthenticated } = useAuthenticationStatus();
  const isLoggedIn = isAuthenticated();
  const groupRequest = isLoggedIn ? getGroup : getUnauthorizedGroup;
  return useQuery<GroupDto, ErrorResponse, GroupWithMetadata>(
    ['group', { groupId }, isLoggedIn],
    () => groupRequest({ groupId }),
    {
      enabled: Boolean(groupId),
      retry: false,
      staleTime: Infinity, // Set to Infinity because this data has a pretty low update rate.
      select: (data) => ({
        ...data,
        isUserAdmin: !isLoggedIn
          ? false
          : data?.metadata?.currentUser?.role === 'owner' ||
            data?.metadata?.currentUser?.role === 'admin',
        isUserMember: !isLoggedIn
          ? false
          : data?.metadata?.currentUser?.status === 'approved',
        isUserPending: !isLoggedIn
          ? false
          : data?.metadata?.currentUser?.status === 'pending',
        isUserRejected: !isLoggedIn
          ? false
          : data?.metadata?.currentUser?.status === 'rejected'
      })
    }
  );
}
export function useGetGroupEvents({
  groupId,
  pageNumber,
  limit,
  afterStartDate,
  beforeStartDate,
  includeDrafts
}: {
  groupId: string;
  pageNumber?: string;
  limit?: string;
  afterStartDate?: string;
  beforeStartDate?: string;
  includeDrafts?: boolean;
}) {
  return useQuery<EventData, ErrorResponse, EventData>(
    ['groupEvents', { groupId, pageNumber }],
    () =>
      getGroupEvents({
        groupId,
        pageNumber,
        limit,
        afterStartDate,
        beforeStartDate,
        includeDrafts
      }),
    {
      enabled: Boolean(groupId),
      retry: false,
      staleTime: Infinity, // Set to Infinity because this data has a pretty low update rate.
      select: (data) => ({
        ...data,
        limit: data.limit || 0,
        currentPage: data.currentPage || 0,
        totalPages: data.totalPages || 0,
        items: data.items || []
      })
    }
  );
}

export function useGetAssociation({
  associationId
}: {
  associationId: string;
}) {
  const { isAuthenticated } = useAuthenticationStatus();
  const isLoggedIn = isAuthenticated();
  const associationRequest = isLoggedIn
    ? getAssociation
    : getUnauthorizedAssociation;
  return useQuery<AssociationDto, ErrorResponse, AssociationDtoWithMetadata>(
    ['association', { associationId }, isLoggedIn],
    () => associationRequest({ associationId }),
    {
      enabled: Boolean(associationId),
      retry: false,
      staleTime: Infinity, // Set to Infinity because this data has a pretty low update rate.
      select: (data) => {
        const status = data.currentUserInfo?.associationMembership?.status;
        return {
          ...data,
          isUserAdmin: !isLoggedIn
            ? false
            : data.currentUserInfo?.associationMembership?.role === 'admin',
          isUserMember: !isLoggedIn
            ? false
            : data.currentUserInfo?.associationMembership?.role === 'member' &&
              status === 'approved',
          isUserPending: !isLoggedIn ? false : status === 'pending',
          isUserRejected: !isLoggedIn ? false : status === 'rejected'
        };
      }
    }
  );
}

export const useGetAssociationVerificationStatus = (associationId: string) =>
  useQuery<{ verificationStatus: 'complete' | 'incomplete' | 'in_progress' }>(
    ['associationVerificationStatus', associationId],
    () => getAssociationVerificationStatus({ associationId }),
    {
      refetchOnWindowFocus: false
    }
  );

export const useGenerateAssociationRequirementsLink = () =>
  useMutation<{ url: string }, ErrorResponse, { associationId: string }>(
    ({ associationId }) =>
      generateAssociationRequirementsLink({ associationId })
  );

export const useGetAssociationMembers = (
  associationId: string,
  query: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationMembersDto>(
    ['associationMembers', associationId, query],
    (_, fetchCursor) =>
      getAssociationMembers({
        associationId,
        query,
        cursor: fetchCursor
      })
  );

  return {
    data: data?.items || [],
    ...rest
  };
};

export const useGetAssociationAffiliatedMembers = (
  associationId: string,
  query: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationMembersDto>(
    ['associationAffiliatedMembers', associationId, query],
    (_, fetchCursor) =>
      getAssociationAffiliatedMembers({
        associationId,
        query,
        cursor: fetchCursor
      })
  );

  return {
    data: data?.items || [],
    ...rest
  };
};

export const useGetAssociationMemberRequests = (
  associationId: string,
  query: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationMembersDto>(
    ['associationMemberRequests', associationId, query],
    (_, fetchCursor) =>
      getAssociationMemberRequests({
        associationId,
        query,
        cursor: fetchCursor
      })
  );

  return {
    data: data?.items || [],
    ...rest
  };
};

export const useGetAssociationAdmins = (
  associationId: string,
  query: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationMembersDto>(
    ['associationAdmins', associationId, query],
    (_, fetchCursor) =>
      getAssociationAdmins({
        associationId,
        query,
        cursor: fetchCursor
      })
  );

  return {
    data: data?.items || [],
    ...rest
  };
};

export function useUpdateMemberStatus() {
  return useMutation(updateAssociationMemberStatus, {
    onSettled: () => {
      queryClient.invalidateQueries('associationAdmins');
      queryClient.invalidateQueries('associationMembers');
      queryClient.invalidateQueries('associationMemberRequests');
      queryClient.invalidateQueries('associationAffiliatedMembers');
    }
  });
}

export function useRemoveAssociationMember() {
  return useMutation(removeAssociationMember, {
    onSettled: () => {
      queryClient.invalidateQueries('associationAdmins');
      queryClient.invalidateQueries('associationMembers');
      queryClient.invalidateQueries('associationMemberRequests');
      queryClient.invalidateQueries('associationAffiliatedMembers');
    }
  });
}

export const useGetAssociationRequestsToPublish = (
  associationId: string,
  numberOfItems?: number,
  sort?: AssociationPublishRequestSortBy,
  order?: 'asc' | 'desc',
  enabled?: boolean,
  query?: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationRequestsToPublishDto>(
    [
      'associationRequestsToPublish',
      associationId,
      numberOfItems,
      sort,
      order,
      query
    ],
    (_, fetchCursor) =>
      getAssociationRequestsToPublish({
        associationId,
        cursor: fetchCursor,
        numberOfItems,
        sort,
        order,
        query
      }),
    {
      keepPreviousData: true,
      select: (data) => ({
        ...data,
        // currentPage is 0-indexed, bumping it here to make thing easier everywhere.
        currentPage: data.currentPage + 1
      }),
      enabled: enabled ?? true
    }
  );

  return {
    data: data || null,
    ...rest
  };
};

export const useGetAssociationEventsLog = (
  associationId: string,
  numberOfItems?: number,
  sort?: AssociationEventLogSortBy,
  order?: 'asc' | 'desc',
  query?: string
) => {
  const { data, ...rest } = useCursorQuery<AssociationEventLogDto>(
    ['associationEventsLog', associationId, numberOfItems, sort, order, query],
    (_, fetchCursor) =>
      getAssociationEventsLog({
        associationId,
        cursor: fetchCursor,
        numberOfItems,
        order,
        sort,
        query
      }),
    {
      keepPreviousData: true,
      select: (data) => ({
        ...data,
        currentPage: data.currentPage + 1
      })
    }
  );

  return {
    data: data || null,
    ...rest
  };
};

export function useCreateGroup() {
  return useMutation<GroupDto, ErrorResponse, CreateOrganizationMutationData>(
    createGroup
  );
}
export function useCreateGroupAlbum() {
  return useMutation<
    AlbumDto,
    ErrorResponse,
    { groupId: string; album: CreateAlbumDto }
  >(({ groupId, album }) => createGroupAlbum(groupId, album));
}

export function useUpdateGroupAlbum() {
  return useMutation<
    AlbumDto,
    ErrorResponse,
    { groupId: string; albumId: string; album: CreateAlbumDto }
  >(({ groupId, albumId, album }) => updateGroupAlbum(groupId, albumId, album));
}

export function useDeleteGroupAlbum() {
  return useMutation<
    AlbumDto,
    ErrorResponse,
    { groupId: string; albumId: string }
  >(({ groupId, albumId }) => deleteGroupAlbum(groupId, albumId));
}

export function useUpdateGroup() {
  return useMutation<GroupDto, ErrorResponse, UpdateOrganizationMutationData>(
    updateGroup,
    {
      onSettled: (_data, _error, variables) => {
        queryClient.invalidateQueries([
          'group',
          { groupId: variables.groupId }
        ]);
      }
    }
  );
}

export function useUpdateAssociation() {
  return useMutation<
    AssociationDto,
    ErrorResponse,
    UpdateAssociationMutationData
  >(updateAssociation, {
    onSettled: (result, error, params) => {
      queryClient.invalidateQueries([
        'association',
        { associationId: params.associationId }
      ]);
    }
  });
}

export function useConfirmStudentEmail() {
  return useMutation<
    { associationId: string; email: string },
    ErrorResponse,
    {
      associationId: string;
      email: string;
    }
  >(confirmStudentEmail, {
    onSettled: (result, error, params) => {
      queryClient.invalidateQueries([
        'association',
        { associationId: params.associationId }
      ]);
    }
  });
}

export function useConfirmStudentEmailCode() {
  return useMutation<
    { associationId: string; code: string },
    ErrorResponse,
    {
      associationId: string;
      code: string;
    }
  >(confirmEmailCode, {
    onSettled: (result, error, params) => {
      queryClient.invalidateQueries([
        'association',
        { associationId: params.associationId }
      ]);
      queryClient.invalidateQueries('associationAdmins');
      queryClient.invalidateQueries('associationMembers');
      queryClient.invalidateQueries('associationMemberRequests');
      queryClient.invalidateQueries('associationAffiliatedMembers');
    }
  });
}

export function useUploadExternalContacts() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    UploadExternalContactsMutationData
  >(uploadExternalContacts);
}

export function useMessageExternalContacts() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    MessageExternalContactsMutationData
  >(messageExternalContacts);
}

export function useGetExternalContactsCount({ groupId }: { groupId: string }) {
  return useQuery<GetExternalContactsCountDto, ErrorResponse>(
    ['group/externalContactsCount', { groupId }],
    () => getExternalContactsCount(groupId),
    { enabled: Boolean(groupId), retry: false }
  );
}
export function useRemoveMember() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    { userId; groupId }
  >(removeMember, {
    onSettled: () => {
      queryClient.invalidateQueries('orgMembers');
      queryClient.invalidateQueries('groupMembers');
    }
  });
}

export function useChangeMemberRole() {
  return useMutation<
    GenericSuccessResponse,
    ErrorResponse,
    { userId; groupId; role }
  >(changeMemberRole, {
    onSettled: () => {
      queryClient.invalidateQueries('orgMembers');
      queryClient.invalidateQueries('groupMembers');
    }
  });
}

export function useGetAllOrganizationMembers(
  organizationId: string,
  enabled: boolean,
  totalMembers: number
) {
  const { data, ...rest } = useQuery<GroupMembersDto, ErrorResponse>(
    ['allOrgMemberIds', { organizationId }],
    () =>
      getGroupMembers({
        groupId: organizationId,
        role: `${GroupMemberRoleEnum.MEMBER},${GroupMemberRoleEnum.ADMIN},${GroupMemberRoleEnum.OWNER}`,
        limit: totalMembers
      }),
    {
      enabled: Boolean(organizationId) && enabled, // Only enable when selectAll is true
      retry: false
    }
  );

  return {
    data: data?.items || [],
    ...rest
  };
}
export function useGetOrganizationMembers({
  role,
  organizationId,
  search,
  limit = 20
}: {
  role?: string;
  organizationId: string;
  search?: string;
  limit?: number;
}) {
  const [fetchPageNumber, setFetchPageNumber] = useState(0);

  const { data, ...orgData } = useQuery<GroupMembersDto, ErrorResponse>(
    ['orgMembers', { role, organizationId, search, fetchPageNumber, limit }],
    () =>
      getGroupMembers({
        groupId: organizationId,
        role,
        query: search,
        pageNumber: fetchPageNumber,
        limit
      }),
    {
      enabled: Boolean(organizationId),
      retry: false
    }
  );

  useEffect(() => {
    // this fixes issue with searching and pagination
    // need to reset current page to 0 when searching
    const currentPage =
      data?.items?.length < data?.limit && search && search !== ''
        ? 0
        : fetchPageNumber;
    if (currentPage !== fetchPageNumber) {
      setFetchPageNumber(currentPage);
    }
  }, [search, data?.items]);

  // TODO: Make this into a shareable hook, like the useCursorQuery one
  const hasNextPage = data?.currentPage + 1 < data?.totalPages;
  const hasPreviousPage = data?.currentPage > 0;

  const fetchNextPage = () => {
    if (hasNextPage) {
      setFetchPageNumber(data.currentPage + 1);
    }
  };

  const fetchPreviousPage = () => {
    if (hasPreviousPage) {
      setFetchPageNumber(data.currentPage - 1);
    }
  };

  return {
    ...orgData,
    data: (data?.items || []) as GroupMemberDto[],
    hasNextPage,
    hasPreviousPage,
    fetchPreviousPage,
    fetchNextPage,
    limit,
    totalPages: data?.totalPages || 0
  };
}

export function useGetOrganizationPendingMembers({
  organizationId,
  search,
  enabled
}: {
  organizationId: string;
  search?: string;
  enabled?: boolean;
}) {
  const [fetchPageNumber, setFetchPageNumber] = useState(0);
  const { data, ...pendingData } = useQuery<GroupMembersDto, ErrorResponse>(
    ['orgPendingMembers', { organizationId, search, fetchPageNumber }],
    () =>
      getPendingGroupMembers({
        groupId: organizationId,
        query: search,
        pageNumber: fetchPageNumber,
        limit: 20
      }),
    {
      enabled: Boolean(organizationId && enabled),
      retry: false
    }
  );

  // TODO: Make this into a shareable hook, like the useCursorQuery one
  const hasNextPage = data?.currentPage + 1 < data?.totalPages;
  const hasPreviousPage = data?.currentPage > 0;

  const fetchNextPage = () => {
    if (hasNextPage) {
      setFetchPageNumber(data.currentPage + 1);
    }
  };

  const fetchPreviousPage = () => {
    if (hasPreviousPage) {
      setFetchPageNumber(data.currentPage - 1);
    }
  };

  return {
    ...pendingData,
    data: (data?.items || []) as GroupMemberDto[],
    hasNextPage,
    hasPreviousPage,
    fetchNextPage,
    fetchPreviousPage
  };
}

export function useApproveOrgMember() {
  return useMutation(approveMemberRequest, {
    onSettled: () => {
      queryClient.invalidateQueries('orgPendingMembers');
      queryClient.invalidateQueries('orgMembers');
      queryClient.invalidateQueries('groupMembers');
      queryClient.invalidateQueries('groupPendingMembers');
    }
  });
}

export function useRejectOrgMember() {
  return useMutation(rejectMemberRequest, {
    onSettled: () => {
      queryClient.invalidateQueries('orgPendingMembers');
      queryClient.invalidateQueries('orgMembers');
      queryClient.invalidateQueries('groupMembers');
      queryClient.invalidateQueries('groupPendingMembers');
    }
  });
}
